import { Box, Card, Center, Group, Loader, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconBuildingStore, IconCar, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { podatki, type Shema } from '../api/odjemalec';
import { cenaOglasa, koliko } from '../oblika/oblika';
import { barve } from '../tema';
import { javniApi, prikazanaVozila, slikaUrl, vgrajeno, VOZILA, type Vrstni } from './javno';
import { NiPonudbe } from './NiPonudbe';
import { PostavitevPonudbe } from './Postavitev';

type Vozilo = Shema['JavnoVozilo'];
type Prodajalec = Shema['JavniProdajalec'];

/**
 * Javna stran celotne zaloge prodajalca (brez prijave, stalna povezava /ponudba/<oznaka>, tudi vgrajena na stranko
 * spletno stran): kartice vozil s sliko, povzetkom in ceno, iskanje in razvrščanje (na strani, zaloga je majhna).
 */
export function JavnaZaloga() {
  const { oznaka = '' } = useParams();
  const { search } = useLocation();
  const vgrajena = vgrajeno(search);
  const [iskanje, setIskanje] = useState('');
  const [vrstni, setVrstni] = useState<Vrstni>('novo');
  const zaloga = useQuery({
    queryKey: ['javna-zaloga', oznaka],
    queryFn: ({ signal }) =>
      podatki(javniApi.GET('/api/javno/ponudba/{oznaka}', { signal, params: { path: { oznaka } } })),
  });
  useEffect(() => {
    if (zaloga.data?.prodajalec?.naziv) {
      document.title = `Zaloga vozil – ${zaloga.data.prodajalec.naziv}`;
    }
  }, [zaloga.data]);

  if (zaloga.isPending) {
    return (
      <Center h="100dvh">
        <Loader aria-label="Nalagam" />
      </Center>
    );
  }
  if (!zaloga.data) {
    return <NiPonudbe napaka={zaloga.error} ponovi={() => void zaloga.refetch()} />;
  }
  const { prodajalec, vozila = [] } = zaloga.data;
  const prikazana = prikazanaVozila(vozila, iskanje, vrstni);

  return (
    <PostavitevPonudbe prodajalec={prodajalec} vgrajena={vgrajena}>
      <Group justify="space-between" align="flex-end" mb="md" gap="sm">
        <div>
          <Title order={1} size="h2">
            Zaloga vozil
          </Title>
          <Text size="sm" c="dimmed">
            {koliko(vozila.length, VOZILA)} na zalogi
          </Text>
        </div>
        {vozila.length > 1 && (
          <IskanjeVozil iskanje={iskanje} setIskanje={setIskanje} vrstni={vrstni} setVrstni={setVrstni} />
        )}
      </Group>
      {prikazana.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {vozila.length === 0 ? 'Trenutno ni vozil na zalogi.' : 'Ni vozil za iskanje.'}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
          {prikazana.map((v) => (
            <KarticaVozila key={v.id} oznaka={oznaka} vozilo={v} iskanje={vgrajena ? '?vgrajeno=1' : ''} />
          ))}
        </SimpleGrid>
      )}
    </PostavitevPonudbe>
  );
}

/** Iskanje in razvrščanje nad seznamom vozil; {@code dodatno} = še ena izbira (npr. prodajalec). */
export function IskanjeVozil({
  iskanje,
  setIskanje,
  vrstni,
  setVrstni,
  dodatno,
}: {
  iskanje: string;
  setIskanje: (v: string) => void;
  vrstni: Vrstni;
  setVrstni: (v: Vrstni) => void;
  dodatno?: ReactNode;
}) {
  return (
    <Group gap="xs" wrap="wrap" style={{ flex: '1 1 320px', maxWidth: dodatno ? 760 : 520 }} justify="flex-end">
      <TextInput
        aria-label="Iskanje"
        placeholder="Iskanje (znamka, model …)"
        leftSection={<IconSearch size={16} />}
        value={iskanje}
        onChange={(e) => setIskanje(e.currentTarget.value)}
        style={{ flex: '1 1 180px' }}
      />
      {dodatno}
      <Select
        aria-label="Razvrsti"
        data={[
          { value: 'novo', label: 'Najnovejša' },
          { value: 'cena-gor', label: 'Najcenejša' },
          { value: 'cena-dol', label: 'Najdražja' },
        ]}
        value={vrstni}
        onChange={(v) => setVrstni((v as Vrstni) ?? 'novo')}
        allowDeselect={false}
        w={140}
      />
    </Group>
  );
}

/** Kartica vozila na seznamu; s {@code prodajalec} (ponudba vseh) še, kdo vozilo prodaja. */
export function KarticaVozila({
  oznaka,
  vozilo,
  iskanje,
  prodajalec,
}: {
  oznaka: string;
  vozilo: Vozilo;
  iskanje: string;
  prodajalec?: Prodajalec;
}) {
  const slika = vozilo.slike?.[0];
  return (
    <Card
      component={Link}
      to={`/ponudba/${oznaka}/${vozilo.id}${iskanje}`}
      radius="lg"
      shadow="xs"
      padding={0}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Box style={{ aspectRatio: '4 / 3', background: barve.izbrano, overflow: 'hidden' }}>
        {slika ? (
          <img
            src={slikaUrl(oznaka, slika, true)}
            alt={vozilo.naziv}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Center h="100%">
            <IconCar size={56} stroke={1.2} color={barve.besediloSekundarno} />
          </Center>
        )}
      </Box>
      <Stack gap={4} p="md">
        <Text fw={700} size="lg" lineClamp={2}>
          {vozilo.naziv}
        </Text>
        {vozilo.povzetek && (
          <Text size="sm" c="dimmed">
            {vozilo.povzetek}
          </Text>
        )}
        <Text fw={700} size="xl" c={barve.primarna} mt={4}>
          {vozilo.cena != null ? cenaOglasa(vozilo.cena) : 'Cena po dogovoru'}
        </Text>
        {prodajalec && (
          <Group gap={6} wrap="nowrap" mt={6} pt={8} style={{ borderTop: `1px solid ${barve.obroba}` }}>
            <IconBuildingStore size={16} color={barve.besediloSekundarno} style={{ flexShrink: 0 }} aria-hidden />
            <Text size="sm" truncate>
              <Text span size="sm" fw={600}>
                {prodajalec.naziv}
              </Text>
              {prodajalec.kraj && (
                <Text span size="sm" c="dimmed">
                  {' · '}
                  {prodajalec.kraj}
                </Text>
              )}
            </Text>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
